import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Job } from '../types';
import StatusBadge from './ui/StatusBadge';
import { useElapsedTime } from '../hooks/useElapsedTime';
import { formatDuration } from '../utils/time';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  History,
  Loader2,
  RefreshCw,
  XCircle,
  FileText,
  RotateCcw,
  X,
  ExternalLink,
} from 'lucide-react';

type PartialResult = {
  stage: string;
  updated_at: string;
  counts: Record<string, number>;
  raw_design: Record<string, unknown[]>;
  page_progress?: {
    completed: number;
    total: number;
    percent: number;
    completed_pages?: number[];
  };
};

interface JobStatusPanelProps {
  jobId: string;
  fileName?: string;
  onStatusChange?: (job: Job) => void;
  compact?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  aborted: 'Canceled',
  needs_review: 'Needs Review',
  waiting_for_rate_limit: 'Waiting (rate limit)',
};

export default function JobStatusPanel({
  jobId,
  fileName,
  onStatusChange,
  compact = false,
}: JobStatusPanelProps) {
  const [job, setJob] = useState<Job | null>(null);
  const jobRef = useRef<Job | null>(null);
  if (jobRef.current !== job) {
    jobRef.current = job;
  }
  const onStatusChangeRef = useRef(onStatusChange);
  if (onStatusChangeRef.current !== onStatusChange) {
    onStatusChangeRef.current = onStatusChange;
  }
  const [logs, setLogs] = useState<Array<{ level: string; message: string; stage: string | null }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<PartialResult | null>(null);
  const [partialTab, setPartialTab] = useState('forms');
  const activeJobIdRef = useRef(jobId);
  const navigate = useNavigate();

  const isDone = (j: Job | null) =>
    j != null && ['completed', 'failed', 'aborted', 'needs_review'].includes(j.status);

  const friendlyFailure = (job: Job) => {
    const msg = (job.error_message || '').toLowerCase();
    if (msg.includes('daily_quota_exhausted') || msg.includes('daily quota') || msg.includes('daily quota exhausted')) {
      return {
        reason: 'The configured LLM provider has exhausted its daily free-model quota.',
        action: 'Wait for the provider quota to reset, add provider credits, or switch to another configured provider/model. Automatic retries are paused for this provider.',
      };
    }
    if (msg.includes('rate limit') || msg.includes('429') || msg.includes('quota')) {
      return {
        reason: 'The extraction service is temporarily rate limited.',
        action: 'Wait a few minutes and refresh, or retry with a different LLM provider.',
      };
    }
    if (msg.includes('timeout') || msg.includes('timed out')) {
      return {
        reason: 'The extraction step took longer than expected and timed out.',
        action: 'Retry the job; if it keeps failing, try a smaller protocol or a faster provider.',
      };
    }
    if (msg.includes('parse') || msg.includes('json')) {
      return {
        reason: 'The extraction output could not be parsed.',
        action: 'Retry the job or switch to a more reliable LLM provider.',
      };
    }
    if (msg.includes('aborted') || msg.includes('cancel')) {
      return {
        reason: 'The job was canceled.',
        action: 'Retry to resume extraction.',
      };
    }
    return {
      reason: 'Extraction failed.',
      action: 'Check the logs below for details, then retry or contact support.',
    };
  };

  const isNewer = (candidate: Job, current: Job | null) => {
    if (!current) return true;
    const candidateTime = new Date(candidate.updated_at).getTime();
    const currentTime = new Date(current.updated_at).getTime();
    if (candidateTime !== currentTime) return candidateTime > currentTime;
    return candidate.status !== current.status
      || candidate.progress_percent !== current.progress_percent
      || candidate.current_stage !== current.current_stage;
  };

  const loadJob = async () => {
    try {
      const data = await apiClient.get<Job>(`/jobs/${jobId}`);
      if (!isNewer(data, jobRef.current)) return;
      setJob(data);
      setError(null);
      if (onStatusChangeRef.current) onStatusChangeRef.current(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unable to load job status';
      setError(msg);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await apiClient.get<Array<{ level: string; message: string; stage: string | null }> | { items?: Array<{ level: string; message: string; stage: string | null }> }>(
        `/jobs/${jobId}/logs?limit=10`
      );
      setLogs(Array.isArray(response) ? response : (response.items || []));
    } catch {
      // logs are optional; don't surface as a main error
    }
  };

  const loadPartial = async () => {
    try {
      const response = await apiClient.get<{ partial_result?: PartialResult | null }>(`/jobs/${jobId}/debug`);
      // A previous request can finish after a re-upload changes jobId.
      if (activeJobIdRef.current !== jobId) return;
      setPartial(response.partial_result ?? null);
    } catch {
      // Partial diagnostics are optional while a job is being initialized.
    }
  };

  useEffect(() => {
    activeJobIdRef.current = jobId;
    setJob(null);
    setLogs([]);
    setPartial(null);
    setPartialTab('forms');
    setError(null);
  }, [jobId]);

  const abortJob = async () => {
    if (!job || !['pending', 'running', 'waiting_for_rate_limit'].includes(job.status)) return;
    try {
      await apiClient.delete(`/jobs/${jobId}`);
      loadJob();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unable to cancel job';
      setError(msg);
    }
  };

  const retryJob = async () => {
    try {
      const data = await apiClient.post<{ new_job_id: string; message: string }>(`/jobs/${jobId}/retry`);
      if (data.new_job_id) {
        navigate(`/jobs/${data.new_job_id}`);
      } else {
        loadJob();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unable to retry job';
      setError(msg);
    }
  };

  useEffect(() => {
    loadJob();
    loadLogs();
    loadPartial();
  }, [jobId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isDone(jobRef.current)) return;
      loadJob();
      loadLogs();
      loadPartial();
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId]);

  const time = useElapsedTime({
    startedAt: job?.started_at,
    completedAt: job?.completed_at,
    progressPercent: job?.progress_percent,
    estimatedRemainingSeconds: job?.estimated_remaining_seconds,
    isRunning: !isDone(job),
  });

  type Milestone = {
    stage: string;
    progress_percent: number;
    ts: string;
    timeLabel: string;
    durationSincePrevious: number | null;
  };

  const stageLog = job?.stage_log || [];
  const startedAt = job?.started_at;
  const milestones = useMemo<Milestone[]>(() => {
    const startTs = startedAt ? new Date(startedAt).getTime() : null;
    return stageLog.map((entry, index) => {
      const ts = new Date(entry.ts).getTime();
      const prevTs = index > 0 ? new Date(stageLog[index - 1].ts).getTime() : startTs;
      return {
        ...entry,
        timeLabel: new Date(entry.ts).toLocaleTimeString(),
        durationSincePrevious: prevTs ? Math.round((ts - prevTs) / 1000) : null,
      };
    });
  }, [stageLog, startedAt]);

  const activePageNumbers = useMemo(() => {
    const active = new Set<number>();
    for (const entry of stageLog) {
      const match = entry.stage.match(/ai_extraction_page_(\d+)_(.+)$/);
      if (!match) continue;
      const pageNumber = Number(match[1]);
      if (match[2] === 'started') active.add(pageNumber);
      else active.delete(pageNumber);
    }
    return [...active].sort((a, b) => a - b);
  }, [stageLog]);

  if (error && !job) {
    return (
      <div className="panel p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm">{error}</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="panel p-4 flex items-center gap-3 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading job status…</span>
      </div>
    );
  }

  const statusLabel = STATUS_LABELS[job.status] || job.status.replace(/_/g, ' ');

  const progress = Math.max(0, Math.min(100, job.progress_percent || 0));
  const partialCounts = (job.metadata_?.partial_counts || {}) as Record<string, number>;
  const partialStage = typeof job.metadata_?.partial_stage === 'string'
    ? job.metadata_.partial_stage.replace(/_/g, ' ')
    : null;
  const partialCountLabel = [
    ['forms', partialCounts.forms],
    ['fields', partialCounts.fields],
    ['visits', partialCounts.visits],
  ]
    .filter(([, value]) => typeof value === 'number')
    .map(([label, value]) => `${value} ${label}`)
    .join(' • ');
  const previewTabs = [
    ['visits', 'Visits'],
    ['forms', 'Forms'],
    ['fields', 'Fields'],
    ['ranges', 'Ranges'],
    ['constraints', 'Constraints'],
    ['code_lists', 'Code Lists'],
    ['lab_panels', 'Lab Panels'],
    ['sample_panels', 'Sample Panels'],
    ['criteria', 'I/E'],
  ] as const;
  const previewRows = partial?.raw_design?.[partialTab] || [];

  const StageIcon =
    job.status === 'failed'
      ? XCircle
      : job.status === 'completed'
        ? CheckCircle2
        : Loader2;

  return (
    <div className="panel overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 text-gray-500 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {fileName || `Job ${job.id.slice(0, 8)}`}
          </h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={job.status} />
          <button
            onClick={loadJob}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            title="Refresh status"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <StageIcon
            className={`h-5 w-5 shrink-0 ${
              job.status === 'failed'
                ? 'text-red-500'
                : job.status === 'completed'
                  ? 'text-green-500'
                  : 'text-indigo-500 animate-spin'
            }`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {statusLabel}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {job.friendly_stage || job.current_stage || 'queued'}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {progress}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {time.elapsedLabel}
            </div>
          </div>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              job.status === 'failed'
                ? 'bg-red-500'
                : job.status === 'completed'
                  ? 'bg-green-500'
                  : 'bg-indigo-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {partialStage && partialCountLabel && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-200">Live data:</span>{' '}
            {partialCountLabel} ({partialStage})
          </div>
        )}

        {partial?.page_progress && partial.page_progress.total > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-200">Page extraction:</span>{' '}
            {partial.page_progress.completed}/{partial.page_progress.total} pages completed
            {activePageNumbers.length > 0 && (
              <span> · in flight: {activePageNumbers.map((page) => `p.${page}`).join(', ')}</span>
            )}
          </div>
        )}

        {!compact && partial && (
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center justify-between gap-2">
              <div className="text-xs font-medium text-blue-800 dark:text-blue-200">
                Live extraction preview · {partial.stage.replace(/_/g, ' ')}
              </div>
              <span className="text-[11px] text-blue-600 dark:text-blue-300">Updates automatically</span>
            </div>
            <div className="flex gap-1 overflow-x-auto px-2 py-2 border-b border-blue-100 dark:border-blue-900">
              {previewTabs.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setPartialTab(key)}
                  className={`whitespace-nowrap px-2 py-1 text-[11px] rounded ${partialTab === key ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  {label} ({partial.counts[key] ?? 0})
                </button>
              ))}
            </div>
            <div className="max-h-48 overflow-y-auto p-2 space-y-1">
              {previewRows.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">This pass has not produced data yet.</p>
              ) : previewRows.slice(0, 100).map((row, index) => {
                const item = row as Record<string, unknown>;
                const name = item.form_name || item.visit_name || item.field_name || item.constraint_text || item.list_name || item.panel_name || item.criteria_type || item.name || `Item ${index + 1}`;
                const detail = item.source_span || item.description || item.field_label || item.normalized_constraint_text || item.category || '';
                return (
                  <div key={`${partialTab}-${index}`} className="flex items-start justify-between gap-3 text-xs border-b border-gray-100 dark:border-gray-700 last:border-0 py-1">
                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{String(name)}</span>
                    <span className="text-gray-500 dark:text-gray-400 text-right truncate">{String(detail)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!compact && (
          <>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                <p className="text-gray-500 dark:text-gray-400">Elapsed</p>
                <p className="font-medium text-gray-900 dark:text-white">{time.elapsedLabel}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                <p className="text-gray-500 dark:text-gray-400">Estimated remaining</p>
                <p className="font-medium text-gray-900 dark:text-white">{time.etaLabel || '—'}</p>
              </div>
              {job.completed_at && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 col-span-2">
                  <p className="text-gray-500 dark:text-gray-400">Completed at</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {new Date(job.completed_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {job.error_message && (
              <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                <div className="flex items-start gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p className="font-medium">
                    {friendlyFailure(job).reason}
                  </p>
                </div>
                <p className="ml-6 text-red-600 dark:text-red-200">
                  {friendlyFailure(job).action}
                </p>
                <details className="ml-6 mt-1 text-red-800/70 dark:text-red-300/70">
                  <summary className="cursor-pointer">Raw error</summary>
                  <div className="break-words mt-1">{job.error_message}</div>
                </details>
              </div>
            )}

            {logs.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Recent logs
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                  {logs.map((log, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-gray-600 dark:text-gray-300"
                    >
                      <span className="font-medium shrink-0">{log.level}</span>
                      <span className="break-words">{log.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {milestones.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1">
                  <History className="h-3.5 w-3.5" />
                  Progress milestones
                </p>
                <div className="max-h-48 overflow-y-auto space-y-1 text-xs">
                  {milestones.map((m, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-2 rounded bg-gray-50 dark:bg-gray-700/50 p-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-gray-700 dark:text-gray-200 w-9 shrink-0">
                          {m.progress_percent}%
                        </span>
                        <span className="text-gray-600 dark:text-gray-300 truncate">
                          {m.stage.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="text-right shrink-0 text-gray-500 dark:text-gray-400">
                        <div>{m.timeLabel}</div>
                        {m.durationSincePrevious != null && (
                          <div className="text-[10px]">+{formatDuration(m.durationSincePrevious)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2">
          {['pending', 'running', 'waiting_for_rate_limit'].includes(job.status) && (
            <button
              onClick={abortJob}
              className="btn-danger text-xs inline-flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          )}
          {(job.status === 'failed' || job.status === 'aborted' || job.status === 'needs_review') && (
            <button
              onClick={retryJob}
              className="btn-primary text-xs inline-flex items-center gap-1"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </button>
          )}
          {job.study_id && isDone(job) && (
            <button
              onClick={() => navigate(`/studies/${job.study_id}`)}
              className="btn-primary text-xs"
            >
              Open study
            </button>
          )}
          <button
            onClick={() => navigate(`/jobs/${jobId}`)}
            className="btn-secondary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View details
          </button>
        </div>
      </div>
    </div>
  );
}
